import './HomePage.css';
import { useState, useEffect, useContext } from 'react';
import LogoAnimated from '../Refs/LogoAminated.gif';
import HeroBackground from '../Refs/HeroBackground.jpg';
import {Context} from '../Context';

const HeaderComponent = ({screenSize, mobileMenuOpen, toggleMenu, closeMenu}) => {
  return (
      <header className={`header header-${screenSize}`}>
        <div className="header-content">
          <div className="logo">
            <img src={LogoAnimated} alt="Sofie AI Logo" className={`logo-animated-${screenSize}`} />
            <div className='logo-text-container'>
              <div className={`logo-text-line-1 logo-text-line-1-${screenSize}`}>Sofie</div>
              <div className={`logo-text-line-2 logo-text-line-2-${screenSize}`}>AI Agent</div>
            </div>
          </div>
          
          {/* Desktop Navigation */}
          {screenSize === 'desktop' && (
            <>
              <nav className="nav-menu nav-menu-desktop">
                <a href="#features">Features</a>
                <a href="#pricing">Pricing</a>
                <a href="#about">About</a>
              </nav>
              <div className="auth-buttons auth-buttons-desktop">
                <button className="btn-signup attention-btn" onClick={() => window.location.href='https://meetsofie.com/signup'}>Sign Up</button>
                <button className="btn-login" onClick={() => window.location.href='https://meetsofie.com/console'}>Login</button>
              </div>
            </>
          )}

          {/* Hamburger Menu Button - Tablet & Mobile */}
          {(screenSize === 'tablet' || screenSize === 'mobile') && (
            <button className={`hamburger-menu hamburger-${screenSize}`} onClick={toggleMenu} aria-label="Toggle menu">
              <span className={`hamburger-line ${mobileMenuOpen ? 'open' : ''}`}></span>
              <span className={`hamburger-line ${mobileMenuOpen ? 'open' : ''}`}></span>
              <span className={`hamburger-line ${mobileMenuOpen ? 'open' : ''}`}></span>
            </button>
          )}
        </div>

        {/* Mobile/Tablet Slide-out Menu */}
        {(screenSize === 'tablet' || screenSize === 'mobile') && (
          <div className={`mobile-menu mobile-menu-${screenSize} ${mobileMenuOpen ? 'open' : ''}`}>
            <nav className="mobile-nav-menu">
              <a href="#features" onClick={closeMenu}>Features</a>
              <a href="#pricing" onClick={closeMenu}>Pricing</a>
              <a href="#about" onClick={closeMenu}>About</a>
              <button className="mobile-btn-signup" onClick={() => { window.location.href='https://meetsofie.com/signup'; closeMenu(); }}>Sign Up</button>
              <button className="mobile-btn-login" onClick={() => { window.location.href='https://meetsofie.com/console'; closeMenu(); }}>Login</button>
            </nav>
          </div>
        )}
      </header>
  );
};

const HeroComponent = ({ screenSize }) => {
  const HeroInteractiveComponent = () => {
    const [step, setStep] = useState('permission'); // permission, mode-selection, conversation
    const [conversationMode, setConversationMode] = useState(null); // 'voice' or 'text'
    const [permissionGranted, setPermissionGranted] = useState(false);
    const [isActive, setIsActive] = useState(false);
    const [status, setStatus] = useState('idle'); // idle, listening, speaking
    const [transcript, setTranscript] = useState([]);

    const [aiVoiceLevel, setAiVoiceLevel] = useState(0);
    const [userVoiceLevel, setUserVoiceLevel] = useState(0);

    const [circleOneSize, setCircleOneSize] = useState(100);
    const [circleTwoSize, setCircleTwoSize] = useState(115);
    const [circleThreeSize, setCircleThreeSize] = useState(130);

    const [circleOneThickness, setCircleOneThickness] = useState(3);
    const [circleTwoThickness, setCircleTwoThickness] = useState(3);
    const [circleThreeThickness, setCircleThreeThickness] = useState(3);

    const startingConversations = [
      "Hi, This is Sofie.",
      "What can I do for you today?",
      "Enable voice or text conversation below to get started.",
    ];

    const [animatedBubbles, setAnimatedBubbles] = useState([]);
    const [currentBubbleIndex, setCurrentBubbleIndex] = useState(0);
    const [waveDistortion, setWaveDistortion] = useState(false);

    // Gradually animate circle thicknesses
    useEffect(() => {
      let animationFrame;
      let startTime = Date.now();
      
      const animateThickness = () => {
        const elapsed = (Date.now() - startTime) / 1000; // Convert to seconds
        
        // Use sine waves with different frequencies for natural variation
        const thickness1 = 3 + Math.sin(elapsed * 0.5) * 0.8;
        const thickness2 = 3 + Math.sin(elapsed * 0.4 + 1) * 0.8;
        const thickness3 = 3 + Math.sin(elapsed * 0.6 + 2) * 0.8;
        
        setCircleOneThickness(thickness1);
        setCircleTwoThickness(thickness2);
        setCircleThreeThickness(thickness3);
        
        animationFrame = requestAnimationFrame(animateThickness);
      };
      
      animationFrame = requestAnimationFrame(animateThickness);
      
      return () => cancelAnimationFrame(animationFrame);
    }, []);

    // Trigger bubble animations sequentially
    useEffect(() => {
      if (step === 'permission' && currentBubbleIndex < startingConversations.length) {
        const timer = setTimeout(() => {
          setAnimatedBubbles(prev => [...prev, {
            text: startingConversations[currentBubbleIndex],
            id: currentBubbleIndex,
            status: 'launching' // launching -> floating -> settled
          }]);
          setCurrentBubbleIndex(prev => prev + 1);
        }, currentBubbleIndex * 1500); // Stagger each bubble by 1.5 seconds

        return () => clearTimeout(timer);
      }
    }, [step, currentBubbleIndex]);

    // Update bubble status through animation phases
    useEffect(() => {
      animatedBubbles.forEach((bubble, index) => {
        if (bubble.status === 'launching') {
          // Trigger wave distortion when bubble launches
          setWaveDistortion(true);
          setTimeout(() => setWaveDistortion(false), 1800);
          
          // After pop-up animation, start floating
          setTimeout(() => {
            setAnimatedBubbles(prev => prev.map((b, i) => 
              i === index ? { ...b, status: 'floating' } : b
            ));
          }, 500);
        } else if (bubble.status === 'floating') {
          // After float animation, settle at top (reduced gap)
          setTimeout(() => {
            setAnimatedBubbles(prev => prev.map((b, i) => 
              i === index ? { ...b, status: 'settled' } : b
            ));
          }, 1400);
        }
      });
    }, [animatedBubbles]);

    const handleGrantPermission = async () => {
      try {
        // Request microphone access
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        // Stop the stream immediately after getting permission
        stream.getTracks().forEach(track => track.stop());
        setPermissionGranted(true);
        setConversationMode('voice');
        setStep('conversation');
      } catch (error) {
        console.error('Permission denied:', error);
        // Default to text conversation if microphone permission is denied
        setPermissionGranted(false);
        setConversationMode('text');
        setStep('conversation');
      }
    };

    const handleTextMode = () => {
      setConversationMode('text');
      setStep('conversation');
    };

    const handleSwitchToVoice = async () => {
      try {
        // Request microphone access
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        // Stop the stream immediately after getting permission
        stream.getTracks().forEach(track => track.stop());
        setPermissionGranted(true);
        setConversationMode('voice');
      } catch (error) {
        console.error('Permission denied:', error);
        // Stay in text mode if permission is denied
      }
    };

    const handleStart = () => {
      setIsActive(true);
      setStatus('listening');
      
      // Simulate user voice input
      const userInterval = setInterval(() => {
        setUserVoiceLevel(Math.random() * 0.8 + 0.2);
      }, 100);
      
      // Simulate conversation
      setTimeout(() => {
        addTranscript('user', 'Hello, I need help scheduling a meeting.');
        setStatus('speaking');
        clearInterval(userInterval);
        setUserVoiceLevel(0);
        
        // Simulate AI voice output
        const aiInterval = setInterval(() => {
          setAiVoiceLevel(Math.random() * 0.8 + 0.2);
        }, 100);
        
        setTimeout(() => {
          addTranscript('agent', 'Of course! I can help you with that. What time works best for you?');
          setStatus('listening');
          clearInterval(aiInterval);
          setAiVoiceLevel(0);
        }, 2000);
      }, 1000);
    };

    const handleStop = () => {
      setIsActive(false);
      setStatus('idle');
      setAiVoiceLevel(0);
      setUserVoiceLevel(0);
    };

    const handleClear = () => {
      setTranscript([]);
    };

    const addTranscript = (type, text) => {
      setTranscript(prev => [...prev, { type, text, timestamp: Date.now() }]);
    };

    const visualizationStyles = {
      '--circle-1-size': `${circleOneSize}px`,
      '--circle-2-size': `${circleTwoSize}px`,
      '--circle-3-size': `${circleThreeSize}px`,
      '--circle-1-border-Thickness': `${circleOneThickness}px`,
      '--circle-2-border-Thickness': `${circleTwoThickness}px`,
      '--circle-3-border-Thickness': `${circleThreeThickness}px`,
    };

    return (
      <div className="hero-interactive-component">
        {/* Permission Overlay */}
        {step === 'permission' && (
          <div className="permission-overlay">
            <div className="permission-content">
              <div className="conversation-window">
                {/* Settled bubbles at the top */}
                {animatedBubbles.filter(b => b.status === 'settled').map((bubble) => (
                  <div key={bubble.id} className="chat-bubble settled">
                    {bubble.text}
                  </div>
                ))}
              </div>
              <div className="wave-container" style={visualizationStyles}>
                {/* Launching and floating bubbles */}
                {animatedBubbles.filter(b => b.status !== 'settled').map((bubble) => (
                  <div 
                    key={bubble.id} 
                    className={`chat-bubble ${bubble.status}`}
                  >
                    {bubble.text}
                  </div>
                ))}
                <div className={`wave-visualization ${isActive ? 'active' : ''} ${waveDistortion ? 'wave-distortion' : ''}`}>
                  {waveDistortion && <div className="wave-overlay"></div>}
                  <div className="circle-container">
                    <div className="circle-scale-wrapper">
                      <div className="flowing-circle circle-1"></div>
                    </div>
                    <div className="circle-scale-wrapper">
                      <div className="flowing-circle circle-2"></div>
                    </div>
                    <div className="circle-scale-wrapper">
                      <div className="flowing-circle circle-3"></div>
                    </div>
                  </div>
                </div>
              </div>
              <div className='permission-btn-group'>
                <button className="permission-btn attention-btn" onClick={handleGrantPermission}>
                  Enable Voice Conversation
                </button>
                <button className="permission-btn attention-btn" onClick={handleTextMode}>
                  Stick with Text Conversation
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Conversation Interface */}
        {step === 'conversation' && (
          <>
            {/* Wave Visualization - Always show circles */}
            <div className="wave-container" style={visualizationStyles}>
              <div className={`wave-status ${status}`}>
                {status === 'idle' && 'Ready'}
                {status === 'listening' && '🎤 Listening...'}
                {status === 'speaking' && '🔊 Speaking...'}
              </div>
              <div className={`wave-visualization ${isActive ? 'active' : ''}`}>
                <div className="circle-container">
                  <div className="circle-scale-wrapper">
                    <div className="flowing-circle circle-1"></div>
                  </div>
                  <div className="circle-scale-wrapper">
                    <div className="flowing-circle circle-2"></div>
                  </div>
                  <div className="circle-scale-wrapper">
                    <div className="flowing-circle circle-3"></div>
                  </div>
                </div>
              </div>
            </div>

            {/* Transcript - Only show for text mode */}
            {conversationMode === 'text' && (
              <div className="transcript-container">
                {transcript.length === 0 ? (
                  <div className="transcript-empty">
                    Start a conversation to see the transcript here
                  </div>
                ) : (
                  transcript.map((msg, idx) => (
                    <div key={idx} className="transcript-message">
                      <div className={`transcript-label ${msg.type}`}>
                        {msg.type === 'user' ? 'You' : 'Sofie AI'}
                      </div>
                      <div className="transcript-text">{msg.text}</div>
                    </div>
                  ))
                )}
              </div>
            )}

            {/* Control Buttons */}
            <div className="control-buttons">
              {conversationMode === 'text' && (
                <button className="control-btn" onClick={handleSwitchToVoice}>
                  🎤 Enable Voice
                </button>
              )}
              {!isActive ? (
                <button className="control-btn" onClick={handleStart}>
                  ▶️ Start Demo
                </button>
              ) : (
                <button className="control-btn stop" onClick={handleStop}>
                  ⏹️ Stop
                </button>
              )}
              {conversationMode === 'text' && (
                <button 
                  className="control-btn" 
                  onClick={handleClear}
                  disabled={transcript.length === 0}
                >
                  🗑️ Clear
                </button>
              )}
            </div>
          </>
        )}
      </div>
    );
  };

  return (
      <section className={`hero hero-${screenSize}`} style={{ backgroundImage: `url(${HeroBackground})`, backgroundSize: 'cover', backgroundPosition: 'center' }}>
        <div className={`hero-content hero-content-${screenSize}`}>
          <div className={`hero-left hero-left-${screenSize}`}>
            <h1 className="hero-title">Unlock More Hours, More Customers, More Growth</h1>
            <p className="hero-subtitle">
              Top performers are already shifting to AI-powered workflows—don't get left behind in the old ways of doing business.
              <br /><br />
              Start your free trial today and let Sofie multiply your results.
            </p>
            <div className="hero-buttons">
              <button className="hero-btn-primary attention-btn">Start Free Trial</button>
              <button className="hero-btn-secondary">See a Demo</button>
            </div>
          </div>
          <div className={`hero-right hero-right-${screenSize}`}>
            <HeroInteractiveComponent />
          </div>
        </div>
      </section>
  )
};

const DemoComponent = ({ screenSize }) => {
  return (
    <section id="demo" className={`demo demo-${screenSize}`}>
      {/* Placeholder for future demo component */}
    </section>
  );
};

const FeaturesComponent = ({ screenSize }) => {
  return (
      <section id="features" className={`features features-${screenSize}`}>
        <div className="section-content">
          <h2 className="section-title">Features</h2>
          <div className={`features-grid features-grid-${screenSize}`}>
            <div className="feature-card">
              <div className="feature-icon">🚀</div>
              <h3>Fast & Reliable</h3>
              <p>Lightning-fast performance with 99.9% uptime guarantee</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">🔒</div>
              <h3>Secure</h3>
              <p>Enterprise-grade security to protect your data</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">📊</div>
              <h3>Analytics</h3>
              <p>Powerful insights to drive your business forward</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">🤝</div>
              <h3>Collaboration</h3>
              <p>Work seamlessly with your team in real-time</p>
            </div>
          </div>
        </div>
      </section>
  );
};

const PricingComponent = ({ screenSize }) => {
  return (
     <section id="pricing" className={`pricing pricing-${screenSize}`}>
        <div className="section-content">
          <h2 className="section-title">Simple Pricing</h2>
          <div className={`pricing-grid pricing-grid-${screenSize}`}>
            <div className="pricing-card">
              <h3>Starter</h3>
              <div className="price">
                <span className="currency">$</span>
                <span className="amount">29</span>
                <span className="period">/month</span>
              </div>
              <ul className="features-list">
                <li>✓ Up to 5 users</li>
                <li>✓ 10GB storage</li>
                <li>✓ Basic support</li>
                <li>✓ Core features</li>
              </ul>
              <button className="btn-pricing">Choose Plan</button>
            </div>
            <div className="pricing-card featured">
              <div className="popular-badge">Popular</div>
              <h3>Professional</h3>
              <div className="price">
                <span className="currency">$</span>
                <span className="amount">79</span>
                <span className="period">/month</span>
              </div>
              <ul className="features-list">
                <li>✓ Up to 20 users</li>
                <li>✓ 100GB storage</li>
                <li>✓ Priority support</li>
                <li>✓ Advanced features</li>
              </ul>
              <button className="btn-pricing">Choose Plan</button>
            </div>
            <div className="pricing-card">
              <h3>Enterprise</h3>
              <div className="price">
                <span className="currency">$</span>
                <span className="amount">199</span>
                <span className="period">/month</span>
              </div>
              <ul className="features-list">
                <li>✓ Unlimited users</li>
                <li>✓ Unlimited storage</li>
                <li>✓ 24/7 support</li>
                <li>✓ All features</li>
              </ul>
              <button className="btn-pricing">Choose Plan</button>
            </div>
          </div>
        </div>
      </section>
  );
};

const FooterComponent = () => {
  return (
    <footer className="footer">
      <div className="footer-content">
        <p>&copy; 2025 Sofie. All rights reserved.</p>
      </div>
    </footer>
  );
};

const HomePage = () => {
  const contextValue = useContext(Context);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [screenSize, setScreenSize] = useState('desktop');

  const toggleMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const closeMenu = () => {
    setMobileMenuOpen(false);
  };

  useEffect(() => {
    const width = contextValue.screenWidth;
    console.log('Screen width:', width);
    if (width <= 480) {
      setScreenSize('mobile');
    } else if (width <= 768) {
      setScreenSize('tablet');
    } else {
      setScreenSize('desktop');
    }
  }, [contextValue]);

  const headerProps = {
    screenSize,
    mobileMenuOpen,
    toggleMenu,
    closeMenu
  };

  const heroProps = {
    screenSize,

  };

  const demoProps = {
    // Add any props needed for DemoComponent in the future
  };

  const featuresProps = {
    screenSize
  };

  const pricingProps = {
    screenSize
  };

  const footerProps = {
    // Add any props needed for FooterComponent in the future
  };

  return (
    <div className={`Home-Page screen-${screenSize}`}>
      <HeaderComponent {...headerProps} />
      <HeroComponent {...heroProps} />
      <DemoComponent {...demoProps} />
      <FeaturesComponent {...featuresProps} />
      <PricingComponent {...pricingProps} />
      <FooterComponent {...footerProps} />
    </div>
  )
};

export default HomePage;